import { useState, useEffect, useMemo, useRef } from 'react';
import { Filter, MapPin, ChevronDown, ChevronUp, Sliders, X, AlertCircle } from 'lucide-react';
import { carrierColors, carrierTextColors } from '../utils/helpers';
import AdSenseCard from '../components/AdSenseCard';
import { ADSENSE_CONFIG } from '../config/adsense.config';
import '../styles/PlansPage.css';

const PlansPage = ({ translations, language }) => {
  const t = translations[language];
  const [plansData, setPlansData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProvince, setSelectedProvince] = useState('');
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [filters, setFilters] = useState({
    carriers: [],
    segments: [],
    minData: '',
    maxPrice: '',
    dataOnly: true,
    highEndDevices: true
  });
  const [sortBy, setSortBy] = useState({ field: 'price', order: 'asc' }); // 'price' or 'data', 'asc' or 'desc'
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState(new Set());
  const [hoveredWarning, setHoveredWarning] = useState(null);
  const filtersRef = useRef(null);

  // Carrier plan URLs
  const CARRIER_URLS = {
    'Virgin Plus': 'https://www.virginplus.ca/en/plans/postpaid.html?#!/',
    'Bell': 'https://www.bell.ca/Mobility/Cell_phone_plans/',
    'Rogers': 'https://www.rogers.com/plans',
    'Fido': 'https://www.fido.ca/plans',
    'Telus': 'https://www.telus.com/en/mobility/plans',
    'Koodo': 'https://www.koodomobile.com/en/rate-plans',
    'Freedom Mobile': 'https://shop.freedommobile.ca/en-CA/plans'
  };

  // Detect user's province based on geolocation
  useEffect(() => {
    const detectProvince = async () => {
      setDetectingLocation(true);

      if ('geolocation' in navigator) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              timeout: 5000,
              enableHighAccuracy: false
            });
          });

          // Reverse geocode to get province
          const { latitude, longitude } = position.coords;

          // Use a simple province detection based on coordinates
          const province = getProvinceFromCoordinates(latitude, longitude);
          if (province) {
            setSelectedProvince(province);
          }
        } catch (error) {
          console.log('Location detection failed:', error);
          // Default to ON if detection fails
          setSelectedProvince('ON');
        }
      } else {
        // Default to ON if geolocation not available
        setSelectedProvince('ON');
      }

      setDetectingLocation(false);
    };

    detectProvince();
  }, []);

  // Simple province detection based on coordinates (approximate boundaries)
  const getProvinceFromCoordinates = (lat, lng) => {
    // Quebec
    if (lng < -57 && lng > -80 && lat > 45 && lat < 62) return 'QC';
    // Ontario
    if (lng < -74 && lng > -95 && lat > 41.5 && lat < 57) return 'ON';
    // British Columbia
    if (lng < -114 && lng > -139 && lat > 48 && lat < 60) return 'BC';
    // Alberta
    if (lng < -110 && lng > -120 && lat > 49 && lat < 60) return 'AB';
    // Manitoba
    if (lng < -95 && lng > -102 && lat > 49 && lat < 60) return 'MB';
    // Saskatchewan
    if (lng < -102 && lng > -110 && lat > 49 && lat < 60) return 'SK';
    // Nova Scotia
    if (lng < -59.7 && lng > -66.5 && lat > 43 && lat < 47) return 'NS';
    // New Brunswick
    if (lng < -63.7 && lng > -69.1 && lat > 44.5 && lat < 48.1) return 'NB';
    // Newfoundland and Labrador
    if (lng < -52.6 && lng > -67.8 && lat > 46.6 && lat < 60.4) return 'NL';
    // PEI
    if (lng < -61.9 && lng > -64.4 && lat > 45.9 && lat < 47.1) return 'PE';

    // Default to ON if can't determine
    return 'ON';
  };

  // Load plans data from API
  useEffect(() => {
    const loadPlans = async () => {
      try {
        const response = await fetch('https://g3a0a684e8149f7-newgate.adb.ca-montreal-1.oraclecloudapps.com/ords/mobideal/product/plans');
        const apiData = await response.json();

        const plans = apiData.items.map((item, index) => {
          let provincesArray = [];
          try {
            provincesArray = JSON.parse(item.provinces_json);
          } catch (e) {
            console.error('Error parsing provinces for plan:', item.plan_id, e);
            provincesArray = [];
          }

          // Create a unique key based on carrier, segment, data, and provinces
          const uniqueKey = `${item.carrier_name}-${item.segment}-${item.data}-${item.provinces_json}-${index}`;

          return {
            uniqueKey: uniqueKey,
            carrier: item.carrier_name,
            segment: item.segment,
            data: item.data,
            dataUnit: item.data_unit,
            finalPrice: item.final_price,
            provinces: provincesArray,
            planId: item.plan_id,
            planName: item.plan_name,
            dataAmount: item.data_amount,
            dataBonus: (item.data_bonus && item.data_bonus !== 0 && item.data_bonus !== '0') ? item.data_bonus : null,
            price: item.price,
            discountedPrice: item.discounted_price,
            isActive: item.is_active === 'Y'
          };
        });

        console.log('Loaded plans:', plans.length);
        console.log('Sample plan provinces:', plans[0]?.provinces);
        setPlansData(plans.filter(plan => plan.isActive));
        setLoading(false);
      } catch (error) {
        console.error('Error loading plans:', error);
        setLoading(false);
      }
    };

    loadPlans();
  }, []);

  // Get unique values for filters
  const uniqueValues = useMemo(() => {
    const carriers = [...new Set(plansData.map(p => p.carrier))].sort();
    const segments = [...new Set(plansData.map(p => p.segment))].sort();

    return { carriers, segments };
  }, [plansData]);

  // Get all provinces for the dropdown
  const allProvinces = useMemo(() => {
    const provinceSet = new Set();
    plansData.forEach(plan => plan.provinces.forEach(prov => provinceSet.add(prov)));
    return [...provinceSet].sort();
  }, [plansData]);

  // Filter plans based on criteria
  const filteredPlans = useMemo(() => {
    // Use selectedProvince if set, otherwise default to 'ON'
    const provinceToFilter = selectedProvince && selectedProvince.trim() !== '' ? selectedProvince : 'ON';

    return plansData.filter(plan => {
      // Province filter - always filter by province (defaults to ON)
      if (!Array.isArray(plan.provinces) || !plan.provinces.includes(provinceToFilter)) {
        return false;
      }

      // Carrier filter
      if (filters.carriers.length > 0 && !filters.carriers.includes(plan.carrier)) {
        return false;
      }

      // Segment filter
      if (filters.segments.length > 0 && !filters.segments.includes(plan.segment)) {
        return false;
      }

      // Data only filter
      if (filters.dataOnly && plan.dataAmount === 0) {
        return false;
      }

      // High-end devices filter
      if (filters.highEndDevices && plan.segment === 'With Device') {
        // Filter out plans that might not be eligible for high-end devices
        if (plan.dataAmount < 10 || plan.dataAmount === 0 ||
            (plan.carrier === 'Virgin Plus' && (plan.planName?.includes('- LITE') || plan.planName?.includes('- BASIC')))) {
          return false;
        }
      }

      // Min data filter
      if (filters.minData && plan.data < parseFloat(filters.minData)) {
        return false;
      }

      // Max price filter
      if (filters.maxPrice && plan.finalPrice > parseFloat(filters.maxPrice)) {
        return false;
      }

      return true;
    });
  }, [plansData, filters, selectedProvince]);

  // Group plans by segment and carrier
  const plansBySegmentAndCarrier = useMemo(() => {
    const byod = {};
    const withDevice = {};

    filteredPlans.forEach(plan => {
      const target = plan.segment === 'BYOD' ? byod : withDevice;
      if (!target[plan.carrier]) {
        target[plan.carrier] = [];
      }
      target[plan.carrier].push(plan);
    });

    // Process each carrier's plans: detect duplicates, mark conflicts, keep cheapest
    const processCarrierPlans = (plans, segment) => {
      // Group by data amount and unit (visible columns)
      const plansByData = {};
      plans.forEach(plan => {
        const key = `${plan.dataAmount}-${plan.dataUnit}`;
        if (!plansByData[key]) {
          plansByData[key] = [];
        }
        plansByData[key].push(plan);
      });

      // Keep only cheapest when duplicates exist, mark with conflict
      const processedPlans = [];
      Object.values(plansByData).forEach(duplicates => {
        if (duplicates.length > 1) {
          // For With Device segment, separate BASIC and LITE plans from regular plans
          if (segment === 'With Device') {
            const basicOrLitePlans = duplicates.filter(plan =>
              plan.planName?.includes('- BASIC') || plan.planName?.includes('- LITE')
            );
            const regularPlans = duplicates.filter(plan =>
              !plan.planName?.includes('- BASIC') && !plan.planName?.includes('- LITE')
            );

            // Add all BASIC/LITE plans (no deduplication)
            basicOrLitePlans.forEach(plan => processedPlans.push(plan));

            // For regular plan duplicates (excluding BASIC/LITE), keep cheapest and mark conflict
            if (regularPlans.length > 1) {
              const sortedByPrice = [...regularPlans].sort((a, b) => a.finalPrice - b.finalPrice);
              const cheapest = sortedByPrice[0];
              cheapest.hasPriceConflict = true;
              processedPlans.push(cheapest);
            } else if (regularPlans.length === 1) {
              // Only one regular plan, just add it
              processedPlans.push(regularPlans[0]);
            }
          } else {
            // For BYOD, keep cheapest as before
            const sortedByPrice = [...duplicates].sort((a, b) => a.finalPrice - b.finalPrice);
            const cheapest = sortedByPrice[0];
            cheapest.hasPriceConflict = true;
            processedPlans.push(cheapest);
          }
        } else {
          processedPlans.push(duplicates[0]);
        }
      });

      return processedPlans;
    };

    // Sort plans within each carrier group
    const sortPlans = (plans) => {
      return plans.sort((a, b) => {
        if (sortBy.field === 'price') {
          return sortBy.order === 'asc' ? a.finalPrice - b.finalPrice : b.finalPrice - a.finalPrice;
        } else {
          return sortBy.order === 'asc' ? a.dataAmount - b.dataAmount : b.dataAmount - a.dataAmount;
        }
      });
    };

    Object.keys(byod).forEach(carrier => {
      const processed = processCarrierPlans(byod[carrier], 'BYOD');
      byod[carrier] = sortPlans(processed);
    });

    Object.keys(withDevice).forEach(carrier => {
      const processed = processCarrierPlans(withDevice[carrier], 'With Device');
      withDevice[carrier] = sortPlans(processed);
    });

    return { byod, withDevice };
  }, [filteredPlans, sortBy]);

  const handleMultiSelect = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter(item => item !== value)
        : [...prev[filterType], value]
    }));
  };

  const handleSortChange = (field) => {
    setSortBy(prev => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Toggle section collapse (BYOD or With Device)
  const toggleSectionCollapse = (section) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };


  if (loading) {
    return (
      <div className="plans-page">
        <div className="plans-loading">{t.loadingPhoneData}</div>
      </div>
    );
  }

  return (
    <div className="plans-page">
      {/* Mobile Filters Button */}
      <button
        onClick={() => setFiltersOpen(!filtersOpen)}
        className={`plans-mobile-filters-button ${filtersOpen ? 'active' : ''}`}
      >
        <Sliders size={16} className="plans-mobile-filters-icon" />
      </button>

      {/* Floating Filters Panel */}
      <div className={`plans-filters-panel ${filtersOpen ? 'active' : ''}`}>
        <div className="plans-filters-panel-header">
          <div className="plans-filters-panel-title">
            <Filter size={24} />
            <h2>{t.filters}</h2>
          </div>
          <button
            onClick={() => setFiltersOpen(false)}
            className="plans-filters-close-button"
          >
            <X size={20} />
          </button>
        </div>

        <div className="plans-filters-content">
          <div className="plans-filters-grid">
            {/* Province Selector */}
            <div className="plans-filter-group">
              <label className="plans-filter-label">
                <MapPin size={16} className="inline-icon" />
                {language === 'EN' ? 'Province' : 'Province'}
              </label>
              <select
                id="province-select"
                value={selectedProvince}
                onChange={(e) => setSelectedProvince(e.target.value)}
                className="plans-filter-input"
                disabled={detectingLocation}
              >
                {detectingLocation ? (
                  <option value="">
                    {language === 'EN' ? 'Detecting...' : 'Détection...'}
                  </option>
                ) : (
                  <>
                    <option value="">
                      {language === 'EN' ? 'All Provinces' : 'Toutes les Provinces'}
                    </option>
                    {allProvinces.map(province => (
                      <option key={province} value={province}>{province}</option>
                    ))}
                  </>
                )}
              </select>
            </div>

            {/* Carrier Filter */}
            <div className="plans-filter-group">
              <label className="plans-filter-label">{t.carriers}</label>
              <div className="plans-filter-options">
                {uniqueValues.carriers.map(carrier => (
                  <label key={carrier} className="plans-filter-option">
                    <input
                      type="checkbox"
                      checked={filters.carriers.includes(carrier)}
                      onChange={() => handleMultiSelect('carriers', carrier)}
                    />
                    {carrier}
                  </label>
                ))}
              </div>
            </div>

            {/* Segment Filter */}
            <div className="plans-filter-group">
              <label className="plans-filter-label">
                {language === 'EN' ? 'Plan Type' : 'Type de Forfait'}
              </label>
              <div className="plans-filter-options">
                {uniqueValues.segments.map(segment => (
                  <label key={segment} className="plans-filter-option">
                    <input
                      type="checkbox"
                      checked={filters.segments.includes(segment)}
                      onChange={() => handleMultiSelect('segments', segment)}
                    />
                    {language === 'EN'
                      ? segment
                      : segment === 'BYOD'
                        ? 'Sans Appareil'
                        : segment === 'With Device'
                          ? 'Avec Appareil'
                          : segment
                    }
                  </label>
                ))}
              </div>
            </div>

            {/* Data Only Filter */}
            <div className="plans-filter-group">
              <label className="plans-filter-label">
                {language === 'EN' ? 'Data Plans' : 'Forfaits Données'}
              </label>
              <div className="plans-filter-options">
                <label className="plans-filter-option">
                  <input
                    type="checkbox"
                    checked={filters.dataOnly}
                    onChange={() => setFilters(prev => ({ ...prev, dataOnly: !prev.dataOnly }))}
                  />
                  {language === 'EN' ? 'Data Only' : 'Données Seulement'}
                </label>
              </div>
            </div>

            {/* High-end Devices Filter */}
            <div className="plans-filter-group">
              <label className="plans-filter-label">
                {language === 'EN' ? 'Device Compatibility' : 'Compatibilité Appareil'}
              </label>
              <div className="plans-filter-options">
                <label className="plans-filter-option">
                  <input
                    type="checkbox"
                    checked={filters.highEndDevices}
                    onChange={() => setFilters(prev => ({ ...prev, highEndDevices: !prev.highEndDevices }))}
                  />
                  {t.highEndDevicesPlans}
                </label>
              </div>
            </div>

            {/* Data & Price Filters */}
            <div className="plans-filter-group">
              <label className="plans-filter-label">
                {language === 'EN' ? 'Min Data (GB)' : 'Données Min (Go)'}
              </label>
              <input
                type="number"
                placeholder="0"
                value={filters.minData}
                onChange={(e) => setFilters(prev => ({ ...prev, minData: e.target.value }))}
                className="plans-filter-input"
              />
            </div>

            <div className="plans-filter-group">
              <label className="plans-filter-label">
                {language === 'EN' ? 'Max Price' : 'Prix Max'}
              </label>
              <input
                type="number"
                placeholder={t.any}
                value={filters.maxPrice}
                onChange={(e) => setFilters(prev => ({ ...prev, maxPrice: e.target.value }))}
                className="plans-filter-input"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="plans-content">
        {/* Plans Display */}
        <div className="plans-quad-container">
          {/* Desktop Filters Section */}
          <div className="desktop-filters-section" ref={filtersRef}>
            <div className="segment-header">
              <h2>{t.filters}</h2>
            </div>
            <div className="desktop-filters-content">
              <div className="plans-filters-grid">
                {/* Province Selector */}
                <div className="plans-filter-group">
                  <label className="plans-filter-label">
                    <MapPin size={16} className="inline-icon" />
                    {language === 'EN' ? 'Province' : 'Province'}
                  </label>
                  <select
                    id="province-select-desktop"
                    value={selectedProvince}
                    onChange={(e) => setSelectedProvince(e.target.value)}
                    className="plans-filter-input"
                    disabled={detectingLocation}
                  >
                    {detectingLocation ? (
                      <option value="">
                        {language === 'EN' ? 'Detecting...' : 'Détection...'}
                      </option>
                    ) : (
                      <>
                        <option value="">
                          {language === 'EN' ? 'All Provinces' : 'Toutes les Provinces'}
                        </option>
                        {allProvinces.map(province => (
                          <option key={province} value={province}>{province}</option>
                        ))}
                      </>
                    )}
                  </select>
                </div>

                {/* Carrier Filter */}
                <div className="plans-filter-group">
                  <label className="plans-filter-label">{t.carriers}</label>
                  <div className="plans-filter-options">
                    {uniqueValues.carriers.map(carrier => (
                      <label key={carrier} className="plans-filter-option">
                        <input
                          type="checkbox"
                          checked={filters.carriers.includes(carrier)}
                          onChange={() => handleMultiSelect('carriers', carrier)}
                        />
                        {carrier}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Segment Filter */}
                <div className="plans-filter-group">
                  <label className="plans-filter-label">
                    {language === 'EN' ? 'Plan Type' : 'Type de Forfait'}
                  </label>
                  <div className="plans-filter-options">
                    {uniqueValues.segments.map(segment => (
                      <label key={segment} className="plans-filter-option">
                        <input
                          type="checkbox"
                          checked={filters.segments.includes(segment)}
                          onChange={() => handleMultiSelect('segments', segment)}
                        />
                        {language === 'EN'
                          ? segment
                          : segment === 'BYOD'
                            ? 'Sans Appareil'
                            : segment === 'With Device'
                              ? 'Avec Appareil'
                              : segment
                        }
                      </label>
                    ))}
                  </div>
                </div>

                {/* Data Only Filter */}
                <div className="plans-filter-group">
                  <label className="plans-filter-label">
                    {language === 'EN' ? 'Data Plans' : 'Forfaits Données'}
                  </label>
                  <div className="plans-filter-options">
                    <label className="plans-filter-option">
                      <input
                        type="checkbox"
                        checked={filters.dataOnly}
                        onChange={() => setFilters(prev => ({ ...prev, dataOnly: !prev.dataOnly }))}
                      />
                      {language === 'EN' ? 'Data Only' : 'Données Seulement'}
                    </label>
                  </div>
                </div>

                {/* High-end Devices Filter */}
                <div className="plans-filter-group">
                  <label className="plans-filter-label">
                    {language === 'EN' ? 'Device Compatibility' : 'Compatibilité Appareil'}
                  </label>
                  <div className="plans-filter-options">
                    <label className="plans-filter-option">
                      <input
                        type="checkbox"
                        checked={filters.highEndDevices}
                        onChange={() => setFilters(prev => ({ ...prev, highEndDevices: !prev.highEndDevices }))}
                      />
                      {t.highEndDevicesPlans}
                    </label>
                  </div>
                </div>

                {/* Data & Price Filters */}
                <div className="plans-filter-group">
                  <label className="plans-filter-label">
                    {language === 'EN' ? 'Min Data (GB)' : 'Données Min (Go)'}
                  </label>
                  <input
                    type="number"
                    placeholder="0"
                    value={filters.minData}
                    onChange={(e) => setFilters(prev => ({ ...prev, minData: e.target.value }))}
                    className="plans-filter-input"
                  />
                </div>

                <div className="plans-filter-group">
                  <label className="plans-filter-label">
                    {language === 'EN' ? 'Max Price' : 'Prix Max'}
                  </label>
                  <input
                    type="number"
                    placeholder={t.any}
                    value={filters.maxPrice}
                    onChange={(e) => setFilters(prev => ({ ...prev, maxPrice: e.target.value }))}
                    className="plans-filter-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Plans Tables or No Results */}
          {filteredPlans.length === 0 ? (
            <div className="plans-no-results-wrapper">
              <div className="plans-no-results">
                <AlertCircle size={48} />
                <p>{t.noResultsMessage}</p>
              </div>
            </div>
          ) : (
            <>
              {/* BYOD Plans Section */}
              {Object.keys(plansBySegmentAndCarrier.byod).length > 0 && (
                <div className={`segment-section ${collapsedSections.has('byod') ? 'collapsed' : ''}`}>
                  <div
                    className="segment-header"
                    onClick={() => toggleSectionCollapse('byod')}
                  >
                    <h2>
                      {language === 'EN' ? 'BYOD Plans' : 'Sans Appareil'}
                    </h2>
                    <button className="collapse-button">
                      {collapsedSections.has('byod') ? (
                        <ChevronDown size={20} />
                      ) : (
                        <ChevronUp size={20} />
                      )}
                    </button>
                  </div>

                  {!collapsedSections.has('byod') && (
                    <div className="plans-table-container">
                      <table className="plans-table">
                        <thead>
                          <tr>
                            <th className="carrier-header-cell"></th>
                            <th
                              className="plan-data-cell clickable-header"
                              onClick={() => handleSortChange('data')}
                            >
                              {language === 'EN' ? 'Data' : 'Données'}
                              {sortBy.field === 'data' && (sortBy.order === 'asc' ? ' ▲' : ' ▼')}
                            </th>
                            <th
                              className="plan-price-cell clickable-header"
                              onClick={() => handleSortChange('price')}
                            >
                              {language === 'EN' ? 'Price' : 'Prix'}
                              {sortBy.field === 'price' && (sortBy.order === 'asc' ? ' ▲' : ' ▼')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(plansBySegmentAndCarrier.byod)
                            .sort(([a], [b]) => {
                              const order = ['Bell', 'Virgin Plus', 'Rogers', 'Fido', 'Telus', 'Koodo', 'Freedom Mobile'];
                              const indexA = order.indexOf(a);
                              const indexB = order.indexOf(b);
                              if (indexA === -1 && indexB === -1) return a.localeCompare(b);
                              if (indexA === -1) return 1;
                              if (indexB === -1) return -1;
                              return indexA - indexB;
                            })
                            .map(([carrier, plans]) =>
                              plans.map((plan, index) => (
                                <tr key={plan.uniqueKey} className={index % 2 === 0 ? 'row-even' : 'row-odd'}>
                                  {index === 0 && (
                                    <td
                                      className="carrier-ribbon-cell"
                                      rowSpan={plans.length}
                                      style={{
                                        backgroundColor: carrierColors[carrier] || carrierColors.default,
                                        borderColor: carrier === 'Virgin Plus' ? '#E5E7EB' : 'transparent'
                                      }}
                                    >
                                      <div className="carrier-text" style={{
                                        color: carrierTextColors[carrier] || carrierTextColors.default
                                      }}>{carrier}</div>
                                    </td>
                                  )}
                                  <td className="plan-data-cell">
                                    <div className="plan-data-display">
                                      <div>
                                        <a
                                          href={CARRIER_URLS[plan.carrier] || '#'}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="plan-cell-link"
                                        >
                                          {plan.dataAmount > 0 ? (
                                            <strong>{plan.dataAmount}{plan.dataUnit}</strong>
                                          ) : (
                                            <span className="plan-unlimited">Talk & Text</span>
                                          )}
                                          {plan.dataBonus && plan.dataBonus > 0 && plan.dataBonus !== '0' && (
                                            <span className="plan-bonus-inline"> <br></br> +{plan.dataBonus}{plan.dataUnit}</span>
                                          )}
                                        </a>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="plan-price-cell">
                                    <div className="plan-price-container">
                                      {plan.discountedPrice && plan.discountedPrice !== plan.price ? (
                                        <>
                                          <div className="plan-price-original">
                                            <a
                                              href={CARRIER_URLS[plan.carrier] || '#'}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="plan-cell-link"
                                            >
                                              ${plan.price}
                                            </a>
                                          </div>
                                          <div className="plan-price-discounted">
                                            <a
                                              href={CARRIER_URLS[plan.carrier] || '#'}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="plan-cell-link"
                                            >
                                              ${plan.finalPrice}/mo
                                            </a>
                                            {plan.hasPriceConflict && (
                                              <div
                                                className="plan-warning-icon price-conflict"
                                                onMouseEnter={() => setHoveredWarning(`price-conflict-${plan.uniqueKey}`)}
                                                onMouseLeave={() => setHoveredWarning(null)}
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  setHoveredWarning(hoveredWarning === `price-conflict-${plan.uniqueKey}` ? null : `price-conflict-${plan.uniqueKey}`);
                                                }}
                                              >
                                                <AlertCircle size={16} />
                                                {hoveredWarning === `price-conflict-${plan.uniqueKey}` && (
                                                  <div className="warning-tooltip">{t.priceConflictWarning}</div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </>
                                      ) : (
                                        <div className="plan-price">
                                          <a
                                            href={CARRIER_URLS[plan.carrier] || '#'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="plan-cell-link"
                                          >
                                            ${plan.finalPrice}/mo
                                          </a>
                                          {plan.hasPriceConflict && (
                                            <div
                                              className="plan-warning-icon price-conflict"
                                              onMouseEnter={() => setHoveredWarning(`price-conflict-${plan.uniqueKey}`)}
                                              onMouseLeave={() => setHoveredWarning(null)}
                                            >
                                              <AlertCircle size={16} />
                                              {hoveredWarning === `price-conflict-${plan.uniqueKey}` && (
                                                <div className="warning-tooltip">{t.priceConflictWarning}</div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* With Device Plans Section */}
              {Object.keys(plansBySegmentAndCarrier.withDevice).length > 0 && (
                <div className={`segment-section ${collapsedSections.has('withDevice') ? 'collapsed' : ''}`}>
                  <div
                    className="segment-header"
                    onClick={() => toggleSectionCollapse('withDevice')}
                  >
                    <h2>
                      {language === 'EN' ? 'With Device' : 'Avec Appareil'}
                    </h2>
                    <button className="collapse-button">
                      {collapsedSections.has('withDevice') ? (
                        <ChevronDown size={20} />
                      ) : (
                        <ChevronUp size={20} />
                      )}
                    </button>
                  </div>

                  {!collapsedSections.has('withDevice') && (
                    <div className="plans-table-container">
                      <table className="plans-table">
                        <thead>
                          <tr>
                            <th className="carrier-header-cell"></th>
                            <th
                              className="plan-data-cell clickable-header"
                              onClick={() => handleSortChange('data')}
                            >
                              {language === 'EN' ? 'Data' : 'Données'}
                              {sortBy.field === 'data' && (sortBy.order === 'asc' ? ' ▲' : ' ▼')}
                            </th>
                            <th
                              className="plan-price-cell clickable-header"
                              onClick={() => handleSortChange('price')}
                            >
                              {language === 'EN' ? 'Price' : 'Prix'}
                              {sortBy.field === 'price' && (sortBy.order === 'asc' ? ' ▲' : ' ▼')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(plansBySegmentAndCarrier.withDevice)
                            .sort(([a], [b]) => {
                              const order = ['Bell', 'Virgin Plus', 'Rogers', 'Fido', 'Telus', 'Koodo', 'Freedom Mobile'];
                              const indexA = order.indexOf(a);
                              const indexB = order.indexOf(b);
                              if (indexA === -1 && indexB === -1) return a.localeCompare(b);
                              if (indexA === -1) return 1;
                              if (indexB === -1) return -1;
                              return indexA - indexB;
                            })
                            .map(([carrier, plans]) =>
                              plans.map((plan, index) => (
                                <tr key={plan.uniqueKey} className={index % 2 === 0 ? 'row-even' : 'row-odd'}>
                                  {index === 0 && (
                                    <td
                                      className="carrier-ribbon-cell"
                                      rowSpan={plans.length}
                                      style={{
                                        backgroundColor: carrierColors[carrier] || carrierColors.default,
                                        borderColor: carrier === 'Virgin Plus' ? '#E5E7EB' : 'transparent'
                                      }}
                                    >
                                      <div className="carrier-text" style={{
                                        color: carrierTextColors[carrier] || carrierTextColors.default
                                      }}>{carrier}</div>
                                    </td>
                                  )}
                                  <td className="plan-data-cell">
                                    <div className="plan-data-display">
                                      <div>
                                        <a
                                          href={CARRIER_URLS[plan.carrier] || '#'}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="plan-cell-link"
                                        >
                                          {plan.dataAmount > 0 ? (
                                            <strong>{plan.dataAmount}{plan.dataUnit}</strong>
                                          ) : (
                                            <span className="plan-unlimited">Talk & Text</span>
                                          )}
                                          {plan.dataBonus && plan.dataBonus > 0 && plan.dataBonus !== '0' && (
                                            <span className="plan-bonus-inline"> +{plan.dataBonus}{plan.dataUnit}</span>
                                          )}
                                        </a>
                                      </div>
                                      {plan.segment === 'With Device' && (
                                        plan.dataAmount < 10 ||
                                        plan.dataAmount === 0 ||
                                        (plan.carrier === 'Virgin Plus' && (plan.planName?.includes('- LITE') || plan.planName?.includes('- BASIC')))
                                      ) && (
                                        <div
                                          className="plan-warning-icon low-data"
                                          onMouseEnter={() => setHoveredWarning(`low-data-${plan.uniqueKey}`)}
                                          onMouseLeave={() => setHoveredWarning(null)}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setHoveredWarning(hoveredWarning === `low-data-${plan.uniqueKey}` ? null : `low-data-${plan.uniqueKey}`);
                                          }}
                                        >
                                          <AlertCircle size={16} />
                                          {hoveredWarning === `low-data-${plan.uniqueKey}` && (
                                            <div className="warning-tooltip">{t.lowDataWarning}</div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="plan-price-cell">
                                    <div className="plan-price-container">
                                      {plan.discountedPrice && plan.discountedPrice !== plan.price ? (
                                        <>
                                          <div className="plan-price-original">
                                            <a
                                              href={CARRIER_URLS[plan.carrier] || '#'}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="plan-cell-link"
                                            >
                                              ${plan.price}
                                            </a>
                                          </div>
                                          <div className="plan-price-discounted">
                                            <a
                                              href={CARRIER_URLS[plan.carrier] || '#'}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="plan-cell-link"
                                            >
                                              ${plan.finalPrice}/mo
                                            </a>
                                            {plan.hasPriceConflict && (
                                              <div
                                                className="plan-warning-icon price-conflict"
                                                onMouseEnter={() => setHoveredWarning(`price-conflict-${plan.uniqueKey}`)}
                                                onMouseLeave={() => setHoveredWarning(null)}
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  setHoveredWarning(hoveredWarning === `price-conflict-${plan.uniqueKey}` ? null : `price-conflict-${plan.uniqueKey}`);
                                                }}
                                              >
                                                <AlertCircle size={16} />
                                                {hoveredWarning === `price-conflict-${plan.uniqueKey}` && (
                                                  <div className="warning-tooltip">{t.priceConflictWarning}</div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </>
                                      ) : (
                                        <div className="plan-price">
                                          <a
                                            href={CARRIER_URLS[plan.carrier] || '#'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="plan-cell-link"
                                          >
                                            ${plan.finalPrice}/mo
                                          </a>
                                          {plan.hasPriceConflict && (
                                            <div
                                              className="plan-warning-icon price-conflict"
                                              onMouseEnter={() => setHoveredWarning(`price-conflict-${plan.uniqueKey}`)}
                                              onMouseLeave={() => setHoveredWarning(null)}
                                            >
                                              <AlertCircle size={16} />
                                              {hoveredWarning === `price-conflict-${plan.uniqueKey}` && (
                                                <div className="warning-tooltip">{t.priceConflictWarning}</div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Ads Column - Desktop Only (>1200px) */}
          <div className="plans-ads-column">
            <div className="segment-header">
              <h2>Ads</h2>
            </div>
            <div className="plans-ads-content">
              <AdSenseCard
                adClient={ADSENSE_CONFIG.CLIENT_ID}
                adSlot={ADSENSE_CONFIG.AD_SLOT}
                showPlaceholder={ADSENSE_CONFIG.SHOW_PLACEHOLDER}
                index={0}
                customHeight="180px"
              />
              <AdSenseCard
                adClient={ADSENSE_CONFIG.CLIENT_ID}
                adSlot={ADSENSE_CONFIG.AD_SLOT}
                showPlaceholder={ADSENSE_CONFIG.SHOW_PLACEHOLDER}
                index={1}
                customHeight="180px"
              />
              <AdSenseCard
                adClient={ADSENSE_CONFIG.CLIENT_ID}
                adSlot={ADSENSE_CONFIG.AD_SLOT}
                showPlaceholder={ADSENSE_CONFIG.SHOW_PLACEHOLDER}
                index={2}
                customHeight="180px"
              />
              <AdSenseCard
                adClient={ADSENSE_CONFIG.CLIENT_ID}
                adSlot={ADSENSE_CONFIG.AD_SLOT}
                showPlaceholder={ADSENSE_CONFIG.SHOW_PLACEHOLDER}
                index={3}
                customHeight="180px"
              />
              <AdSenseCard
                adClient={ADSENSE_CONFIG.CLIENT_ID}
                adSlot={ADSENSE_CONFIG.AD_SLOT}
                showPlaceholder={ADSENSE_CONFIG.SHOW_PLACEHOLDER}
                index={4}
                customHeight="180px"
              />
            </div>
          </div>
        </div>

        {/* Disclaimer Section */}
        <div className="info-section">
          <AlertCircle size={18} />
          <div>
            <p>
              <strong>{t.plansDisclaimerTitle}:</strong> {t.plansDisclaimerBeta}
            </p>
            <p>{t.plansDisclaimerLimitations}</p>
            <ul>
              <li>{t.plansDisclaimerPoint1}</li>
              <li>{t.plansDisclaimerPoint2}</li>
              <li>{t.plansDisclaimerPoint3}</li>
            </ul>
            <p><strong>{t.plansDisclaimerVerify}</strong></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlansPage;

